import React from "react";
import RevenueChart from "./RevenueChart";
import WebsiteReviewsChart from "./WebsiteReviewsChart";

const OutcomesSection = () => {
  return (
    <div className="outcomes-section">
      <h2 className="outcomes-title">📊 Campaign Analysis</h2>

      <div className="outcomes-content">
        <h3>💰 Ad Spend & Lead Generation</h3>
        <div className="statistics-container">
          <div className="statistics-grid">
            <div className="stat-item">
              <span className="stat-label">Raw Total Ad Spend:</span>
              <span className="stat-value">€6,925.85</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">
                Ad Spend for Pre-Campaign Leads:
              </span>
              <span className="stat-value">€5,956.66</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Total Leads from Ads:</span>
              <span className="stat-value">2,384</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Cost per Lead:</span>
              <span className="stat-value">€2.50</span>
            </div>
          </div>
        </div>

        <h3>🔄 Conversion Rates</h3>
        <p>
          From my initial 878 non-ad subscribers, 47 became backers, pledging
          €1,967 in total.
          <strong> Conversion rate: 5.4%</strong>
        </p>
        <p>
          From the 2,384 leads generated through ads, 248 converted into
          backers.
          <strong> Conversion rate: 10.1%</strong>
        </p>

        <h3>💸 Kickstarter Gross</h3>
        <div className="statistics-container">
          <div className="statistics-grid">
            <div className="stat-item">
              <span className="stat-label">
                Kickstarter Gross from pre-campaign Ads Leads:
              </span>
              <span className="stat-value">€4,597.00</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">
                Kickstarter Gross from Leads acquired not through Ads:
              </span>
              <span className="stat-value">€1,996.00</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">
                Total Kickstarter Gross from during-campaign Ads:
              </span>
              <span className="stat-value">€2,542.00</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Total KS Gross:</span>
              <span className="stat-value">€20,702.00</span>
            </div>
          </div>
        </div>

        <h3>📈 Return on Ad Spend (ROAS)</h3>
        <p>
          The numbers below only include gross I could <strong>directly</strong>{" "}
          attribute to the ads.
        </p>
        <div className="statistics-container">
          <div className="statistics-grid">
            <div className="stat-item">
              <span className="stat-label">ROA from pre-campaign efforts:</span>
              <span className="stat-value">
                <strong>77.0%</strong>
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">ROA from All Ads:</span>
              <span className="stat-value">
                <strong>36.7%</strong>
              </span>
            </div>
          </div>
        </div>

        <RevenueChart />

        <div className="key-takeaways">
          <h4>🔑 Key Takeaways</h4>
          <ul>
            <li>
              <strong>Ads were primary revenue drivers</strong> — At least 44%
              of revenue came from advertising efforts (pre-campaign and during
              campaign)
            </li>
            <li>
              <strong>Hidden attribution</strong> — This percentage is likely
              higher, as many 'Other' category backers probably discovered
              through ad-generated leads
            </li>
            <li>
              <strong>Platform contribution</strong> — Kickstarter's organic
              discovery accounted for approximately 25% of total revenue
            </li>
            <li>
              <strong>Limited impact from content creators</strong> — Events and
              content creator promotions generated only 4.6% of campaign revenue
            </li>
          </ul>
        </div>
      </div>

      <div className="content-creators-section">
        <h3>🔍 Deepdive into Instagram Content Creators</h3>
        <p>
          But first I want to run through the Instagram Content Creators and
          Written Reviewers, and their direct contributions to the revenue.
          Let's see if we can back up my hypothesis that the direct revenue from
          these Creators is minimal.
        </p>
        <p>
          I got 123 organic visitors on the Kickstarter page from Instagram.
          Many of the Creators, where on Instagram. Some might have come from my
          profile directly.
        </p>
        <div className="timeline-image-container">
          <img
            src="./images/instagram_googleanalytics.png"
            alt="Instagram impact on Google Analytics"
            className="timeline-image"
          />
          <p className="image-caption">
            Impact of Instagram posts on website traffic
          </p>
        </div>
        <p>
          This is confirmed by the Kickstarter Dashboard, which states only 1
          backer converted from the instagram-reviewers link. The Advanced
          Analytics section on Kickstarter showed 0 backers from Instagram
          reviewers.
        </p>

        <div className="timeline-image-container">
          <img
            src="./images/instagram_kickstarter_revenue.png"
            alt="Instagram Kickstarter revenue"
            className="timeline-image"
          />
          <p className="image-caption">
            Kickstarter Dashboard showing only 1 backer converted from Instagram
            reviewers
          </p>
        </div>

        <WebsiteReviewsChart />

        <div className="brand-awareness-section">
          <h3>💭 Brand Awareness Impact</h3>
          <p>
            We've established direct traffic and sales from content creators was
            minimal. But since I spend{" "}
            <strong>over $2,000 on content creators</strong>, I do want to
            understand if they contributed to brand awareness. So I reviewed
            backers about how they first discovered the project. I received
            responses from <strong>555 of the 772</strong> total backers (still
            increasing). Here are the key results:
          </p>

          <div className="survey-results-table">
            <table>
              <thead>
                <tr>
                  <th>Where they first heard about RR</th>
                  <th>Total Pledged (€)</th>
                  <th>Total Responses</th>
                  <th>Pledge per Response (€)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Kickstarter Discovery</td>
                  <td>6,417.00</td>
                  <td>225</td>
                  <td>28.52</td>
                </tr>
                <tr>
                  <td>Meta Ads</td>
                  <td>3,392.80</td>
                  <td>119</td>
                  <td>28.51</td>
                </tr>
                <tr>
                  <td>Word of Mouth</td>
                  <td>2,449.00</td>
                  <td>51</td>
                  <td>48.02</td>
                </tr>
                <tr>
                  <td>Other / Misc</td>
                  <td>1,485.00</td>
                  <td>49</td>
                  <td>30.31</td>
                </tr>
                <tr>
                  <td>Press / Media</td>
                  <td>1,482.00</td>
                  <td>58</td>
                  <td>25.55</td>
                </tr>
                <tr>
                  <td>Organic Social</td>
                  <td>999.00</td>
                  <td>41</td>
                  <td>24.37</td>
                </tr>
                <tr>
                  <td>Email / Creator Channel</td>
                  <td>198.00</td>
                  <td>8</td>
                  <td>24.75</td>
                </tr>
                <tr>
                  <td>Event</td>
                  <td>47.00</td>
                  <td>4</td>
                  <td>11.75</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p>
            Even considering these survey results, content creator channels
            (Press/Media and Creator Channel categories) accounted for{" "}
            <strong>a relatively small portion</strong> of how backers first
            discovered the project. The data confirms that
            <strong>
              Kickstarter's own discovery mechanisms and paid social media (ads)
              were the primary drivers of awareness
            </strong>
            , with word of mouth showing the highest pledge per response at{" "}
            <strong>€48.02</strong>.
          </p>
          <p>
            This suggests that while content creators may help build
            credibility, they were not significant drivers of initial discovery
            for most backers according to their own recollection.
          </p>
        </div>

        <div className="conclusion">
          <h3>Overall Key Take Aways & Lessons Learned</h3>
          <p>
            The data clearly shows that{" "}
            <strong>
              paid advertising and email marketing were the primary drivers
            </strong>{" "}
            of the River Rats Kickstarter campaign's success.{" "}
            <strong>Content creators and organic social media efforts</strong>,
            while often recommended as essential marketing channels for
            crowdfunding campaigns,{" "}
            <strong>contributed minimally to the overall revenue</strong>.
          </p>
          <p>
            For future campaigns, allocating more resources to building an email
            list pre-launch and running targeted ads during the campaign would
            likely yield the highest return on investment. The Kickstarter
            platform itself remains an important discovery channel, accounting
            for about a quarter of the campaign's revenue.
          </p>
          <p>
            This case study demonstrates the importance of data-driven marketing
            decisions and the value of tracking attribution carefully throughout
            a crowdfunding campaign.
          </p>
          <ul>
            <li>
              <strong>No stretch goals:</strong> they <em>don't matter</em> for
              a small game like this.
            </li>
            <li>
              <strong>No more paid content creators, only YouTube:</strong>{" "}
              <em>Only try people who actually enjoy the game.</em>
            </li>
            <li>
              <strong>No more (small) Instagram accounts:</strong>{" "}
              <em>Hardly anything comes out of them</em>, and it's a lot of
              arrangements.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default OutcomesSection;
